// File: src/components/pages/students/DeletionRequestList.jsx
//
// Renders the "Delete" tab content in Manage Students.
// Lists all student account deletion requests for this instructor,
// sorted Pending-first, with Approve / Reject actions on each row.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Trash2, CheckCircle, XCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import { studentDeletionAPI } from '../../../services/api';
import { toast } from '../../../utils/toast';

// ── Status badge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Pending:  { color: '#f59e0b', bg: '#fef3c7' },
    Approved: { color: '#10b981', bg: '#d1fae5' },
    Rejected: { color: '#ef4444', bg: '#fee2e2' },
    Failed:   { color: '#dc2626', bg: '#fee2e2' }
  };
  const { color, bg } = map[status] || map.Failed;
  return (
    <span style={{
      padding: '0.2rem 0.6rem', borderRadius: '999px',
      background: bg, color, fontSize: '0.75rem', fontWeight: 700
    }}>
      {status}
    </span>
  );
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Reject reason modal ────────────────────────────────────────────────────────
const RejectModal = ({ request, onConfirm, onClose, isSaving }) => {
  const [reason, setReason] = useState('');
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }}>
      <div style={{
        background: '#fff', borderRadius: '0.75rem', maxWidth: '420px', width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '1.5rem'
      }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#111827' }}>
          Reject deletion request
        </h3>
        <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 1rem' }}>
          <strong>{request.studentName}</strong> — {request.studentEmail}
        </p>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">Reason for rejection (optional)</label>
          <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Account is still active and being used"
            style={{
              width: '100%', padding: '0.6rem 0.85rem',
              border: '1.5px solid #e5e7eb', borderRadius: '0.5rem',
              fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical'
            }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button onClick={onClose} disabled={isSaving}
            style={{ padding: '0.5rem 1rem', border: '1.5px solid #e5e7eb',
                     borderRadius: '0.5rem', background: '#fff', cursor: 'pointer',
                     color: '#374151', fontFamily: 'inherit', fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={() => onConfirm(reason)} disabled={isSaving}
            style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: '0.5rem',
                     background: '#ef4444', color: '#fff', cursor: 'pointer',
                     fontFamily: 'inherit', fontWeight: 600,
                     display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <XCircle size={14} /> {isSaving ? 'Rejecting…' : 'Reject Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const DeletionRequestList = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [requests,     setRequests]     = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [error,        setError]        = useState('');
  const [processing,   setProcessing]   = useState(null); // requestId being processed
  const [rejectTarget, setRejectTarget] = useState(null); // request to reject

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await studentDeletionAPI.getRequests(getAccessTokenSilently);
      if (res.success) setRequests(res.data || []);
      else setError(res.error || 'Failed to load requests');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (request) => {
    if (!window.confirm(
      `Approve deletion for ${request.studentName}?\n\n` +
      `This will permanently delete their Auth0 account and all associated records. This cannot be undone.`
    )) return;

    setProcessing(request.requestId);
    try {
      const res = await studentDeletionAPI.approveRequest(request.requestId, getAccessTokenSilently);
      if (res.success) {
        toast.success(`${request.studentName}'s account has been deleted.`);
        await load();
      } else {
        toast.error(res.error || 'Failed to approve request');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (reason) => {
    if (!rejectTarget) return;
    setProcessing(rejectTarget.requestId);
    try {
      const res = await studentDeletionAPI.rejectRequest(
        rejectTarget.requestId, reason, getAccessTokenSilently
      );
      if (res.success) {
        toast.success('Deletion request rejected.');
        setRejectTarget(null);
        await load();
      } else {
        toast.error(res.error || 'Failed to reject request');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="loading-state" style={{ padding: '3rem 0' }}>
        <Trash2 size={48} className="loading-icon" />
        <h2>Loading deletion requests…</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state" style={{ padding: '3rem 0' }}>
        <AlertCircle size={48} className="error-icon" />
        <h2>Failed to load requests</h2>
        <p className="error-message">{error}</p>
        <button className="btn-primary" onClick={load}><RefreshCw size={14} /> Retry</button>
      </div>
    );
  }

  const pending  = requests.filter(r => r.status === 'Pending');
  const resolved = requests.filter(r => r.status !== 'Pending');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#111827', fontWeight: 700 }}>
            Account Deletion Requests
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
            {pending.length} pending · {resolved.length} resolved
          </p>
        </div>
        <button onClick={load} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.45rem 0.9rem', border: '1.5px solid #e5e7eb',
          borderRadius: '0.5rem', background: '#fff', cursor: 'pointer',
          fontSize: '0.82rem', color: '#374151', fontFamily: 'inherit'
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Empty state */}
      {requests.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '3rem 1rem',
          background: '#f9fafb', borderRadius: '0.75rem',
          border: '1px dashed #e5e7eb'
        }}>
          <CheckCircle size={40} color="#10b981" style={{ marginBottom: '0.75rem' }} />
          <p style={{ color: '#6b7280', fontWeight: 600 }}>No deletion requests</p>
          <p style={{ color: '#9ca3af', fontSize: '0.82rem' }}>
            Requests submitted by students will appear here.
          </p>
        </div>
      )}

      {/* Pending requests */}
      {pending.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{
            fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.6rem'
          }}>Pending</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {pending.map(r => (
              <div key={r.requestId} style={{
                background: '#fff', borderRadius: '0.65rem',
                border: '1.5px solid #fde68a', padding: '1rem 1.25rem',
                display: 'flex', alignItems: 'center', gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>
                    {r.studentName || '—'}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.1rem' }}>
                    {r.studentEmail}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', fontSize: '0.78rem' }}>
                  <Clock size={13} /> Requested {formatDate(r.requestedAt)}
                </div>
                <StatusBadge status={r.status} />
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    onClick={() => handleApprove(r)}
                    disabled={processing === r.requestId}
                    title="Approve — permanently deletes the student account"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.4rem 0.85rem', borderRadius: '0.4rem',
                      border: 'none', background: '#10b981', color: '#fff',
                      fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit', opacity: processing === r.requestId ? 0.6 : 1
                    }}>
                    <CheckCircle size={13} />
                    {processing === r.requestId ? '…' : 'Approve'}
                  </button>
                  <button
                    onClick={() => setRejectTarget(r)}
                    disabled={processing === r.requestId}
                    title="Reject — keeps the student account active"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.4rem 0.85rem', borderRadius: '0.4rem',
                      border: '1.5px solid #fca5a5', background: '#fff', color: '#ef4444',
                      fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit', opacity: processing === r.requestId ? 0.6 : 1
                    }}>
                    <XCircle size={13} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolved requests */}
      {resolved.length > 0 && (
        <div>
          <p style={{
            fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.6rem'
          }}>Resolved</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {resolved.map(r => (
              <div key={r.requestId} style={{
                background: '#f9fafb', borderRadius: '0.65rem',
                border: '1px solid #f3f4f6', padding: '0.75rem 1.25rem',
                display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap'
              }}>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>
                    {r.studentName || r.studentEmail || '—'}
                  </div>
                  {r.rejectionReason && (
                    <div style={{ fontSize: '0.74rem', color: '#6b7280', marginTop: '0.15rem' }}>
                      Rejected: {r.rejectionReason}
                    </div>
                  )}
                  {r.failureReason && (
                    <div style={{ fontSize: '0.74rem', color: '#dc2626', marginTop: '0.15rem' }}>
                      ⚠ Failed: {r.failureReason}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.74rem', color: '#9ca3af' }}>
                  Processed {formatDate(r.processedAt)}
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          request={rejectTarget}
          onConfirm={handleReject}
          onClose={() => setRejectTarget(null)}
          isSaving={processing === rejectTarget.requestId}
        />
      )}
    </div>
  );
};

export default DeletionRequestList;